import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { Armchair, Coffee, Gift, Luggage, Sparkles, UtensilsCrossed, Wifi, Zap } from 'lucide-react';
import { describeTripRewards, tripRewardCategoryLabel } from '@/utils/tripRewards';

const ICONS = {
  priority_boarding: Zap,
  extra_legroom: Armchair,
  seat_selection: Armchair,
  checked_bag: Luggage,
  lounge_access: Coffee,
  wifi: Wifi,
  food_drink: UtensilsCrossed,
  other: Gift,
};

/**
 * The customer's "Complimentary" banner for rewards the team added through
 * Flight Details → Edit → Rewarded (src/constants/tripRewards.js). Built like
 * the trip page's Trustpilot banner (founder, 2026-10-25), in gold. Same
 * promise as the "We've added … to your trip" email: on us, nothing to do,
 * they'll see it at check-in.
 */
export default function TripRewardsCard({ rewards, airlineName = '' }) {
  if (!rewards?.length) return null;
  const one = rewards.length === 1 ? rewards[0] : null;
  const Icon = one ? (ICONS[one.category] || Gift) : Gift;
  const title = one ? (
    <>We&apos;ve added <span className="text-[#ecd39c]">{one.name}</span> to your trip</>
  ) : (
    <>We&apos;ve added a few extras to your trip</>
  );
  const what = one
    ? `Complimentary ${tripRewardCategoryLabel(one.category).toLowerCase()}${airlineName ? ` on ${airlineName}` : ''}, as a thank you for booking with Defera.`
    : `${describeTripRewards(rewards)}, on us, as a thank you for booking with Defera.`;
  return (
    <motion.section
      aria-label="Complimentary rewards"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
      className="relative mb-6 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#e3c27e]/[0.08] via-white/[0.03] to-transparent backdrop-blur-sm"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(227,194,126,0.12),_transparent_60%)]" />
      <div className="pointer-events-none absolute -right-16 -top-16 w-48 h-48 rounded-full bg-[#e3c27e]/10 blur-3xl opacity-60" />

      <div className="relative flex items-center gap-3 sm:gap-4 p-4 sm:p-6">
        <div className="flex-shrink-0 w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-[#e3c27e]/15 border border-[#e3c27e]/25 flex items-center justify-center shadow-[0_0_24px_-6px_rgba(227,194,126,0.4)]">
          <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-[#ecd39c]" strokeWidth={1.75} aria-hidden="true" />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-white text-[14px] sm:text-[17px] font-normal tracking-tight leading-snug mb-0.5 sm:mb-1">
            {title}
          </h3>
          <p className="text-white/60 text-[11px] sm:text-sm font-normal leading-relaxed line-clamp-2 sm:line-clamp-none">
            {/* Phones get the short version, so "check in" isn't the part that's cut off. */}
            <span className="sm:hidden">{one ? 'On us, as a thank you.' : `${describeTripRewards(rewards)}, on us.`} You&apos;ll see {one ? 'it' : 'them'} when you check in.</span>
            <span className="hidden sm:inline">{what} Nothing to do: you&apos;ll see {one ? 'it' : 'them'} when you check in.</span>
          </p>
        </div>

        <div className="flex-shrink-0 flex items-center gap-1.5 text-[#ecd39c] text-xs sm:text-sm font-medium opacity-90">
          <span className="hidden sm:inline">On us</span>
          <Sparkles className="w-4 h-4" aria-hidden="true" />
        </div>
      </div>
    </motion.section>
  );
}

TripRewardsCard.propTypes = {
  rewards: PropTypes.arrayOf(PropTypes.shape({
    category: PropTypes.string,
    name: PropTypes.string,
  })),
  airlineName: PropTypes.string,
};
